import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import type { Dayjs } from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrophy,
  faStar,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import {
  addReward,
  assignRewardToUser,
  getCompanyEmployees,
  getRewards,
} from "../repository/data.repository";
import {
  EmployeeUser,
  MAX_PAGE_SIZE,
  Reward,
} from "../repository/data.default";
import { useTokenInfo } from "../hooks/useTokenInfo";
import { getProblemMessage } from "../utils/http";
import { QueryOperation } from "../code/search-query/search-query";

// PD-02 §6.16.5 — Create Company Reward Action. Company users create rewards
// via /Rewards and now (backend permission widened to AppCompanyUser:Create
// on /RewardsAssignment) assign them to employees in their own company. The
// server-side RequireUserTargetAsync enforces the company boundary.
function CreateRewardsPage() {
  const { claims } = useTokenInfo();
  const companyUid = claims?.usrCmpUid;
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [assigning, setAssigning] = useState<Reward | null>(null);

  function load() {
    setLoading(true);
    const conditions = companyUid
      ? [
          {
            field: "companyUid",
            values: [companyUid],
            operation: QueryOperation.Equal,
          },
        ]
      : [];
    getRewards({
      searchQuery: { conditions },
      pageNumber: 0,
      pageSize: 20,
      sortBy: "createdOn",
      sortDescending: true,
    })
      .then((data) => setRewards(data.data ?? []))
      .catch(() => setRewards([]))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  async function onSubmit(values: {
    name: string;
    description?: string;
    requiredPoints: number;
  }) {
    setSubmitting(true);
    try {
      await addReward({
        name: values.name,
        description: values.description,
        requiredPoints: values.requiredPoints,
        companyUid,
      });
      message.success("Reward created.");
      form.resetFields();
      load();
    } catch (err) {
      message.error(getProblemMessage(err) || "Failed to create reward.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Col className="animate__animated animate__fadeIn flex flex-auto flex-col gap-4 overflow-y-auto p-6">
      <Row>
        <Typography.Title level={3} className="!m-0">
          <FontAwesomeIcon icon={faTrophy} className="mr-2" />
          Create Reward
        </Typography.Title>
      </Row>

      <Card className="emp-card">
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            name="name"
            label="Reward name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="e.g. 50% off gym membership" />
          </Form.Item>
          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea rows={3} placeholder="Terms, how to redeem, etc." />
          </Form.Item>
          <Form.Item
            name="requiredPoints"
            label="Required points"
            rules={[
              { required: true, message: "Required points are required" },
              {
                type: "number",
                min: 0,
                message: "Must be a non-negative number",
              },
            ]}
            initialValue={100}
          >
            <InputNumber className="w-full" min={0} />
          </Form.Item>
          <Row className="justify-end">
            <Button type="primary" htmlType="submit" loading={submitting}>
              Create reward
            </Button>
          </Row>
        </Form>
      </Card>

      <Card
        className="emp-card"
        title="Recent rewards"
        bodyStyle={{ padding: 0 }}
      >
        {loading ? (
          <div className="p-4">
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        ) : rewards.length === 0 ? (
          <div className="p-4">
            <Typography.Text type="secondary">
              No rewards yet. Create one above.
            </Typography.Text>
          </div>
        ) : (
          <List
            dataSource={rewards}
            renderItem={(r) => (
              <List.Item
                style={{ paddingLeft: 16, paddingRight: 16 }}
                actions={[
                  <Button
                    key="assign"
                    type="primary"
                    size="small"
                    icon={<FontAwesomeIcon icon={faUserPlus} />}
                    onClick={() => setAssigning(r)}
                  >
                    Assign
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={r.name}
                  description={
                    <Space direction="vertical" size={2} className="w-full">
                      {r.description && (
                        <Typography.Text className="text-xs">
                          {r.description}
                        </Typography.Text>
                      )}
                      <Space wrap size={8}>
                        <Tag
                          color="gold"
                          icon={<FontAwesomeIcon icon={faStar} />}
                        >
                          {r.requiredPoints} pts
                        </Tag>
                        {r.isPublished ? (
                          <Tag color="green">Published</Tag>
                        ) : (
                          <Tag>Draft</Tag>
                        )}
                      </Space>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      <AssignRewardModal
        reward={assigning}
        onClose={() => setAssigning(null)}
      />
    </Col>
  );
}

// PD-02 §6.16.5 — pick one or more employees in your company + optional
// activation / expiration dates. The POST goes to /RewardsAssignment/{reward}/
// User/{user}; backend enforces the caller's company scope on each user.
function AssignRewardModal(props: {
  readonly reward: Reward | null;
  readonly onClose: () => void;
}) {
  const [employees, setEmployees] = useState<EmployeeUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!props.reward) return;
    setLoading(true);
    setSelected([]);
    setRange(null);
    getCompanyEmployees({
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    })
      .then((d) => setEmployees(d.data ?? []))
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to load employees."),
      )
      .finally(() => setLoading(false));
  }, [props.reward]);

  async function onSubmit() {
    if (!props.reward || selected.length === 0) return;
    setSubmitting(true);
    const assignmentDate = range?.[0]?.toISOString();
    const expirationDate = range?.[1]?.toISOString();
    const results = await Promise.allSettled(
      selected.map((userUid) =>
        assignRewardToUser({
          rewardUid: props.reward!.uid,
          userUid,
          assignmentDate,
          expirationDate,
        }),
      ),
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    setSubmitting(false);
    if (failed === 0) {
      message.success(`Reward assigned to ${ok} employee${ok === 1 ? "" : "s"}.`);
      props.onClose();
    } else {
      message.warning(`Assigned to ${ok} of ${results.length}; ${failed} failed.`);
    }
  }

  return (
    <Modal
      open={!!props.reward}
      title={`Assign "${props.reward?.name ?? ""}" to employees`}
      onCancel={props.onClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={props.onClose}>
          Cancel
        </Button>,
        <Button
          key="assign"
          type="primary"
          loading={submitting}
          disabled={selected.length === 0}
          onClick={onSubmit}
        >
          Assign to {selected.length} employee{selected.length === 1 ? "" : "s"}
        </Button>,
      ]}
    >
      <Form layout="vertical">
        <Form.Item label="Employees" required>
          <Select
            mode="multiple"
            placeholder="Pick one or more employees"
            value={selected}
            onChange={setSelected}
            loading={loading}
            optionFilterProp="label"
            options={employees.map((e) => ({
              value: e.uid,
              label:
                [e.firstName, e.lastName].filter(Boolean).join(" ") ||
                e.userName,
            }))}
          />
        </Form.Item>
        <Form.Item label="Active window (optional)">
          <DatePicker.RangePicker
            value={range as any}
            onChange={(v) => setRange(v as any)}
            allowEmpty={[true, true]}
          />
        </Form.Item>
        <Typography.Text type="secondary" className="text-xs">
          Leave the window empty to activate the reward immediately with no
          expiry. A future start date creates it inactive until that moment.
        </Typography.Text>
      </Form>
    </Modal>
  );
}

export default CreateRewardsPage;
